import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import client from '../../api/client';

const SECTIONS = [
  {
    key: 'signoffs',
    label: 'Sign Offs',
    description: 'Signed approval documents from BHEL coordinators',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'emerald',
    useLocation: true,
  },
  {
    key: 'knowledge_base',
    label: 'Knowledge Base',
    description: 'Troubleshooting guides and reference documents',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    color: 'blue',
    useDeviceDropdown: true,
    tagLabel: 'Device',
  },
  {
    key: 'config_files',
    label: 'Configuration Files',
    description: 'Device and room configuration files',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    color: 'violet',
    useDeviceDropdown: true,
    tagLabel: 'Device',
  },
];

const KB_DEVICES = [
  'PTZ Camera',
  'Display / Screen',
  'Dell Micro PC',
  'Jabra PanaCast 50',
  'AV Controller',
  'Crestron Switcher',
  'Switch Matrix',
  'Network Switch',
  'Microphone',
  'Mercury Speaker',
  'HDMI Splitter',
  'Air Media',
  'USB Hub / Extender',
  'Remote Control',
  'Other',
];

const COLOR = {
  emerald: { tab: 'border-emerald-500 text-emerald-600', badge: 'bg-emerald-100 text-emerald-700', icon: 'bg-emerald-100 text-emerald-600', btn: 'bg-emerald-600 hover:bg-emerald-500' },
  blue: { tab: 'border-blue-500 text-blue-600', badge: 'bg-blue-100 text-blue-700', icon: 'bg-blue-100 text-blue-600', btn: 'bg-blue-600 hover:bg-blue-500' },
  violet: { tab: 'border-violet-500 text-violet-600', badge: 'bg-violet-100 text-violet-700', icon: 'bg-violet-100 text-violet-600', btn: 'bg-violet-600 hover:bg-violet-500' },
};

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function fileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return { label: 'PDF', bg: 'bg-red-100 text-red-600' };
  if (['doc', 'docx'].includes(ext)) return { label: 'DOC', bg: 'bg-blue-100 text-blue-600' };
  if (['xls', 'xlsx'].includes(ext)) return { label: 'XLS', bg: 'bg-green-100 text-green-600' };
  if (['cfg', 'conf', 'ini', 'json', 'xml', 'yaml', 'yml', 'lpz', 'vtz', 'sig'].includes(ext)) return { label: ext.toUpperCase(), bg: 'bg-gray-100 text-gray-600' };
  if (['jpg', 'jpeg', 'png'].includes(ext)) return { label: 'IMG', bg: 'bg-purple-100 text-purple-600' };
  return { label: ext.toUpperCase() || 'FILE', bg: 'bg-gray-100 text-gray-500' };
}

function UploadModal({ section, locations, onClose, onUploaded }) {
  const [form, setForm] = useState({ title: '', notes: '', tag: '', location: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select a file.'); return; }
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (section.useLocation && !form.location) { setError('Please select a location.'); return; }
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('section', section.key);
      fd.append('title', form.title.trim());
      fd.append('notes', form.notes.trim());
      if (!section.useLocation) fd.append('tag', form.tag.trim());
      if (section.useLocation && form.location) fd.append('location', form.location);
      fd.append('file', file);
      await client.post('/documents/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-bold text-brand-500 text-base">Upload to {section.label}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* File drop zone */}
          <div
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-brand-300 hover:bg-brand-50 transition-colors"
          >
            {file ? (
              <div className="flex items-center gap-3 justify-center">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${fileIcon(file.name).bg}`}>{fileIcon(file.name).label}</span>
                <span className="text-sm font-medium text-brand-500 truncate max-w-[200px]">{file.name}</span>
                <span className="text-xs text-gray-400">{formatBytes(file.size)}</span>
              </div>
            ) : (
              <>
                <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                <p className="text-sm text-gray-400">Click to browse or drag & drop</p>
                <p className="text-xs text-gray-300 mt-1">PDF, DOC, XLS, CFG, LPZ, JSON, VTZ, SIG, images…</p>
              </>
            )}
            <input
              ref={inputRef} type="file" className="hidden"
              onChange={e => { setFile(e.target.files[0] || null); if (!form.title && e.target.files[0]) setForm(f => ({ ...f, title: e.target.files[0].name.replace(/\.[^.]+$/, '') })); }}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Title *</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Document title"
            />
          </div>

          {section.useLocation ? (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Location *</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 bg-white"
                value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              >
                <option value="">Select a location…</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
          ) : section.useDeviceDropdown ? (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Device</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 bg-white"
                value={form.tag} onChange={e => setForm(f => ({ ...f, tag: e.target.value }))}
              >
                <option value="">Select a device…</option>
                {KB_DEVICES.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">{section.tagLabel}</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                value={form.tag} onChange={e => setForm(f => ({ ...f, tag: e.target.value }))}
                placeholder={section.tagPlaceholder}
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Notes</label>
            <textarea
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-300"
              value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Optional notes"
            />
          </div>

          {error && <p className="text-red-500 text-xs">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 rounded-xl py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={uploading} className={`flex-1 rounded-xl py-2 text-sm font-semibold text-white transition-colors ${COLOR[section.color].btn} disabled:opacity-50`}>
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DocumentsPage() {
  const user = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const visibleSections = SECTIONS.filter(s => !(s.key === 'signoffs' && user.role === 'RE'));
  const [activeSection, setActiveSection] = useState(visibleSections[0]?.key || 'signoffs');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    client.get('/locations/').then(({ data }) => setLocations(data.results || data)).catch(() => {});
  }, []);

  const section = visibleSections.find(s => s.key === activeSection) || visibleSections[0];
  const colors = COLOR[section.color];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/documents/?section=${activeSection}`);
      setDocs(data.results || data);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); setSearch(''); }, [activeSection]);

  const handleDelete = async (id) => {
    try { await client.delete(`/documents/${id}/`); } catch {}
    setDeleteId(null);
    load();
  };

  const filtered = docs.filter(d =>
    d.title.toLowerCase().includes(search.toLowerCase()) ||
    (d.tag || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.notes || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-brand-500">Documents</h1>
        <p className="text-gray-400 text-sm mt-0.5">Sign offs, knowledge base articles, and configuration files</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {visibleSections.map(s => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
              activeSection === s.key
                ? COLOR[s.color].tab
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            <span className={activeSection === s.key ? '' : 'opacity-50'}>{s.icon}</span>
            {s.label}
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${activeSection === s.key ? COLOR[s.color].badge : 'bg-gray-100 text-gray-400'}`}>
              {s.key === activeSection ? filtered.length : ''}
            </span>
          </button>
        ))}
      </div>

      {/* Section header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors.icon}`}>
            {section.icon}
          </div>
          <div>
            <p className="font-bold text-brand-500 text-sm">{section.label}</p>
            <p className="text-gray-400 text-xs">{section.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input
              className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 w-52"
              placeholder="Search documents…"
              value={search} onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowUpload(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-colors ${colors.btn}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            Upload
          </button>
        </div>
      </div>

      {/* Document list */}
      {loading ? (
        <div className="card p-12 text-center text-gray-400 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <div className={`w-12 h-12 rounded-2xl ${colors.icon} flex items-center justify-center mx-auto mb-3`}>{section.icon}</div>
          <p className="font-semibold text-brand-500">{search ? 'No results' : `No ${section.label} yet`}</p>
          <p className="text-gray-400 text-sm mt-1">{search ? 'Try a different search term.' : 'Upload a file to get started.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(doc => {
            const fi = fileIcon(doc.file_name);
            return (
              <div key={doc.id} className="card p-4 flex items-center gap-4 group hover:shadow-md transition-shadow">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-xs ${fi.bg}`}>
                  {fi.label}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-brand-500 text-sm truncate">{doc.title}</p>
                    {doc.location_name && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge}`}>{doc.location_name}</span>
                    )}
                    {!doc.location_name && doc.tag && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge}`}>{doc.tag}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                    <span>{doc.file_name}</span>
                    <span>·</span>
                    <span>{formatBytes(doc.file_size)}</span>
                    {doc.uploaded_by_name && <><span>·</span><span>by {doc.uploaded_by_name}</span></>}
                    <span>·</span>
                    <span>{new Date(doc.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  </div>
                  {doc.notes && <p className="text-xs text-gray-400 mt-1 truncate">{doc.notes}</p>}
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <a
                    href={doc.file_url} download
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Download
                  </a>
                  {deleteId === doc.id ? (
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleDelete(doc.id)} className="px-2 py-1.5 bg-red-500 text-white rounded-lg text-xs font-semibold hover:bg-red-400">Confirm</button>
                      <button onClick={() => setDeleteId(null)} className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50">Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => setDeleteId(doc.id)} className="p-1.5 text-gray-300 hover:text-red-400 rounded-lg hover:bg-red-50 transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showUpload && (
        <UploadModal section={section} locations={locations} onClose={() => setShowUpload(false)} onUploaded={load} />
      )}
    </div>
  );
}
